import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors } from "style";
import { PrimaryButton } from "components/Buttons";
import Card from "components/Card";
import { FormattedMessage } from "react-intl";
import { Label, Select } from "components/Form";
import { InterviewTitle, InterviewSubtitle } from "components/VideoPresentation";
import { useAudioDetection } from "hooks";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { Banner } from "components/Banner";
import { BANNER_TYPE } from "constants/index";

/**
 * SetupStep Component
 *
 * @param {object}    stream
 * @param {object}    devices
 * @param {string}    selectedCameraId
 * @param {string}    selectedMicId
 * @param {string}    selectedSpeakerId
 * @param {function}  onChangeDevices
 * @param {function}  onChangeSpeaker
 * @param {function}  onContinue
 */
const SetupStep = ({
  stream,
  devices,
  selectedCameraId,
  selectedMicId,
  selectedSpeakerId,
  onChangeDevices,
  onChangeSpeaker,
  onContinue,
}) => {
  const videoRef = useRef(null);
  const { startAudioDetection, stopAudioDetection, audioLevel, hasDetectedAudio } = useAudioDetection(stream);

  /* Attach camera stream to preview */
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => {
    startAudioDetection();

    return () => {
      stopAudioDetection();
    };
  }, [stream]);

  const handleSpeakerChange = async (name, value) => {
    const id = value;
    onChangeSpeaker(id);

    if ("setSinkId" in HTMLMediaElement.prototype && videoRef.current) {
      try {
        await videoRef.current.setSinkId(id);
      } catch (_err) {
        alert("Unable to set speaker device");
      }
    }
  };

  return (
    <div css={styles.container}>
      <InterviewTitle titleId="VideoPresentation.SetupStep.Title" />
      <InterviewSubtitle subtitleId="VideoPresentation.SetupStep.Subtitle" />
      <Card css={styles.card}>
        <div css={styles.badge}>
          <FormattedMessage id="VideoPresentation.SetupStep.Badge" />
        </div>

        <div css={styles.layout}>
          <div css={styles.video_col}>
            <video ref={videoRef} autoPlay muted playsInline css={styles.video} />
          </div>

          <div css={styles.form_col}>
            <div css={styles.field_group}>
              <Label>
                <FormattedMessage id="VideoPresentation.SetupStep.Camera" />
              </Label>
              <Select
                value={selectedCameraId}
                onChange={(name, value) => onChangeDevices(value, selectedMicId)}
                options={devices.cameras.map((d) => ({ label: d.label, value: d.deviceId }))}
                valueKey="value"
                labelKey="label"
              />
            </div>

            <div css={styles.field_group}>
              <Label>
                <FormattedMessage id="VideoPresentation.SetupStep.Microphone" />
              </Label>
              <Select
                value={selectedMicId}
                onChange={(name, value) => onChangeDevices(selectedCameraId, value)}
                options={devices.microphones.map((d) => ({ label: d.label, value: d.deviceId }))}
                valueKey="value"
                labelKey="label"
              />
            </div>

            <div css={styles.field_group}>
              <Label>
                <FormattedMessage id="VideoPresentation.SetupStep.Speakers" />
              </Label>
              <Select
                value={selectedSpeakerId}
                onChange={handleSpeakerChange}
                options={devices.speakers.map((d) => ({ label: d.label, value: d.deviceId }))}
                valueKey="value"
                labelKey="label"
              />
            </div>

            <div css={styles.field_group}>
              <div css={styles.audio_meter_row}>
                <Icon type={ICON_TYPE.mic} size={ICON_SIZE.large} color={colors.grayAnatomyDark} />

                <div css={styles.audio_meter_wrapper}>
                  <div css={styles.audio_meter_fill(audioLevel)} />
                </div>
              </div>
            </div>

            <div css={styles.field_group}>
              <Banner isClosable={false} type={hasDetectedAudio ? BANNER_TYPE.info : BANNER_TYPE.warning}>
                <FormattedMessage id={`VideoPresentation.SetupStep.AudioDetection.${hasDetectedAudio ? "Success" : "Warning"}`} />
              </Banner>
            </div>
          </div>
        </div>

        <div css={styles.footer}>
          <PrimaryButton onClick={onContinue} disabled={!hasDetectedAudio}>
            <FormattedMessage id="VideoPresentation.SetupStep.Continue" />
          </PrimaryButton>
        </div>
      </Card>
    </div>
  );
};

const styles = {
  container: css`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  card: css`
    padding: 24px;
    border-radius: 20px;
    box-shadow: 0 8px 20px rgba(17, 24, 39, 0.06);
    display: flex;
    flex-direction: column;
    gap: 24px;
  `,
  badge: css`
    padding: 4px 12px;
    border-radius: 999px;
    background: ${colors.purpleRainLight3};
    border: 1px solid ${colors.purpleRainDark2};
    color: ${colors.purpleRainBase};
    font-size: 12px;
    font-weight: 600;
    align-self: flex-start;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  `,
  layout: css`
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 24px;
  `,
  video_col: css`
    display: flex;
    justify-content: center;
  `,
  video: css`
    width: 100%;
    border-radius: 16px;
    background: ${colors.black};
    transform: scaleX(-1);
    aspect-ratio: 4 / 3;
  `,
  form_col: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  field_group: css`
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
  footer: css`
    display: flex;
    justify-content: center;
    margin-top: 8px;
  `,
  audio_meter_row: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  audio_meter_wrapper: css`
    flex: 1;
    height: 12px;
    border-radius: 999px;
    overflow: hidden;
    background: #f1f1f1;
    position: relative;
  `,
  audio_meter_fill: (level) => css`
    height: 100%;
    width: ${level}%;
    border-radius: 999px;
    transition: width 120ms ease-out;
    background: linear-gradient(
      90deg,
      #d8f5d8 0%,
      #b7ecb7 100%
    );
  `,
};

SetupStep.propTypes = {
  stream: PropTypes.object,
  devices: PropTypes.object.isRequired,
  selectedCameraId: PropTypes.string,
  selectedMicId: PropTypes.string,
  selectedSpeakerId: PropTypes.string,
  onChangeDevices: PropTypes.func.isRequired,
  onChangeSpeaker: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default SetupStep;
